import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

export type SupportedLocale = 'en' | 'ru' | 'de' | 'pt' | 'fr' | 'es';

export const SUPPORTED_LOCALES: SupportedLocale[] = ['en', 'ru', 'de', 'pt', 'fr', 'es'];
export const DEFAULT_LOCALE: SupportedLocale = 'en';

/** BCP47-тег для `Intl.DateTimeFormat` и подобных — подписи дат идут на языке интерфейса. */
export const INTL_LOCALES: Record<SupportedLocale, string> = {
  en: 'en-US',
  ru: 'ru-RU',
  de: 'de-DE',
  pt: 'pt-BR',
  fr: 'fr-FR',
  es: 'es-ES',
};

/**
 * Строку из `i18n.language` (или Telegram) приводит к известной локали —
 * незнакомый код (и пустая строка до инициализации) даёт дефолт.
 */
export function toLocale(language: string | null | undefined): SupportedLocale {
  return SUPPORTED_LOCALES.includes((language ?? '') as SupportedLocale)
    ? (language as SupportedLocale)
    : DEFAULT_LOCALE;
}

interface I18nModule {
  default: Record<SupportedLocale, Record<string, unknown>>;
}

/**
 * Каждый `*.i18n.ts` лежит рядом с компонентом/конфигом, который он
 * переводит (см. `packages/app/CLAUDE.md`-соглашение из плана локализации),
 * а не в одном центральном файле. Namespace i18next = имя файла без
 * расширения — оно и так уникально в этой кодовой базе (имена компонентов).
 * Перевод есть не у всех неймспейсов (словарь пока en/ru) — недостающие
 * ключи добирает `fallbackLng`.
 */
const modules = import.meta.glob<I18nModule>('/src/**/*.i18n.ts', { eager: true });

type ResourcesTree = Record<SupportedLocale, Record<string, Record<string, unknown>>>;

// `addResourceBundle` привязывается к инстансу i18next только внутри
// `.init()` — до него на инстансе такого метода ещё нет. Поэтому ресурсы
// собираются в обычный объект и передаются через `init({ resources })`,
// а не добавляются вызовами до инициализации.
function collectResources(): ResourcesTree {
  const resources = Object.fromEntries(SUPPORTED_LOCALES.map((locale) => [locale, {}])) as ResourcesTree;
  const seenNamespaces = new Set<string>();

  for (const [path, module] of Object.entries(modules)) {
    const fileName = path.split('/').pop() ?? path;
    const namespace = fileName.replace(/\.i18n\.ts$/, '');

    if (import.meta.env.DEV && seenNamespaces.has(namespace)) {
      console.warn(`[i18n] Дублирующийся namespace "${namespace}" — файлы ${path} перезаписывают друг друга`);
    }
    seenNamespaces.add(namespace);

    for (const locale of SUPPORTED_LOCALES) {
      const bundle = module.default[locale];
      if (bundle) resources[locale][namespace] = bundle;
    }
  }

  return resources;
}

/** Вызывается из `bootstrap()` до первого рендера — язык уже известен (Telegram-догадка). */
export async function initI18n(initialLanguage: SupportedLocale): Promise<void> {
  await i18next.use(initReactI18next).init({
    resources: collectResources(),
    lng: initialLanguage,
    fallbackLng: DEFAULT_LOCALE,
    // React уже экранирует вывод — двойное экранирование i18next не нужно.
    interpolation: { escapeValue: false },
    returnEmptyString: false,
  });
}

export { i18next };
